import React, { useMemo } from 'react';
import { Dices, Sparkles, Target, Zap } from 'lucide-react';
import './HeroWelcomeCard.css';
import { getTopRecommendedTopic } from '../utils/topicRecommendation';

/**
 * HeroWelcomeCard Component
 * 
 * Props:
 * @param {object} profile - Student profile object containing username, points, etc.
 * @param {function} onStartRandomQuiz - Callback function when clicking "Surprise Quiz"
 */
export default function HeroWelcomeCard({ profile, subjects, onStartQuiz }) {
  const username = profile?.username;

  const recommendedTopic = useMemo(
    () => getTopRecommendedTopic(subjects),
    [subjects]
  );

  const handleRecommendedClick = () => {
    if (recommendedTopic && onStartQuiz) {
      onStartQuiz({topicId: recommendedTopic.id, topicName: recommendedTopic.name, subjectName: recommendedTopic.subjectName});
    }
  };

  return (
    <div className="hero-banner">
      {/* Decorative Background Elements */}
      <div className="hero-banner__bg-glow" />
      <div className="hero-banner__shape hero-banner__shape--1">✨</div>
      <div className="hero-banner__shape hero-banner__shape--2">🚀</div>

      {/* Content Area */}
      <div className="hero-banner__content">
        <div className="hero-banner__text-zone">
          <div className="hero-banner__greeting-badge">
            <Sparkles size={14} className="hero-banner__sparkle-icon" />
            <span>Ready to learn today?</span>
          </div>

          <h1 className="hero-banner__title">
            Hello{username ? <span className="hero-banner__name">, {username}!</span> : '!'} 👋
          </h1>

          <p className="hero-banner__subtitle">
            Tackle your recommended challenge, roll the dice for a surprise or browse a topic below!
          </p>
        </div>

        {/* Action Zone */}
        <div className="hero-banner__action-zone">
          {recommendedTopic && (
            <button
              type="button"
              className="hero-banner__action-btn hero-banner__recommended-btn"
              onClick={handleRecommendedClick}
              title={`Recommended Challenge: ${recommendedTopic.name}`}
            >
              <div className="hero-banner__btn-icon-wrapper hero-banner__btn-icon-wrapper--recommended">
                <Target size={22} className="hero-banner__target-icon" />
              </div>
              <div className="hero-banner__btn-text">
                <div className="hero-banner__pill-row">
                  <span className="hero-banner__status-pill">
                    <Zap size={10} className="hero-banner__pill-icon" />
                    {recommendedTopic.badgeText || 'Power-Up'}
                  </span>
                </div>
                <span className="hero-banner__btn-title" title={recommendedTopic.name}>
                  {recommendedTopic.name}
                </span>
                <span className="hero-banner__btn-sub">
                  {recommendedTopic.subjectName} • Lvl {recommendedTopic.currentLevel || 1}
                </span>
              </div>
            </button>
          )}
          <button
            className="hero-banner__action-btn hero-banner__surprise-btn"
            onClick={() => onStartQuiz(null)}
            title="Start a quick surprise quiz"
          >
            <div className="hero-banner__btn-icon-wrapper">
              <Dices size={22} className="hero-banner__dice-icon" />
            </div>
            <div className="hero-banner__btn-text">
              <span className="hero-banner__btn-title">Surprise Quiz</span>
              <span className="hero-banner__btn-sub">Random Challenge</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}